import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { defaultsDeep } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { addCommasToNumber, shortenNumber } from "src/app/util/util.service";

const DAYS_SINCE_LAST_VISIT_COLORS = [
  "#7DC4FD",
  "#1D6789",
  "#A1C700",
  "#A1C700",
  "#F0B900",
  "#FF9226",
  "#A00000",
];

const TIME_ON_STATION_COLORS = [
  "#7DC4FD",
  "#1D6789",
  "#46616E",
  "#293A42",
  "#838383",
  "#AAAAAA",
  "#d6d6d6",
];

const DEFAULT_BAR_CHART_OPTIONS = {
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 22,
  },
  maintainAspectRatio: false,
  responsive: true,
  tooltip: {
    trigger: "axis",
    axisPointer: {
      type: "shadow",
    },
  },
  xAxis: {
    show: true,
    type: "category",
    axisLabel: {
      color: "#858997",
    },
    axisLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    max: 6,
  },
  yAxis: {
    type: "value",

    splitLine: {
      show: false,
    },
  },
  series: [
    {
      itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
      type: "bar",
      label: {
        show: true,
        position: "insideBottom",
      },
    },
  ],
};

const DEFUALT_HOR_BAR_CHART_OPTIONS = {
  grid: {
    left: 56,
    top: 0,
    right: 30,
    bottom: 50,
  },
  xAxis: {
    type: "value",
    position: "top",
    axisLabel: { show: false },

    splitLine: {
      show: false,
    },
  },
  yAxis: {
    show: true,
    type: "category",
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
  },
  series: [
    {
      itemStyle: { color: "#A1C700", borderRadius: [0, 5, 5, 0] },
      type: "bar",
      label: {
        show: true,
        position: "right",
      },
    },
  ],
};

const DEFAULT_PIE_CHART_OPTIONS = {
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
  hover: { mode: null },
  legend: {
    show: true,
    icon: "circle",
    bottom: 0,
  },
  tooltip: {
    trigger: "item",
    formatter: (params: any, ticket: any, callback: any) => {
      return `<strong>${params.name}</strong>
      <br/>Percent: ${params.percent}%
      <br/>Total: ${addCommasToNumber(params.value)}`;
    },
  },
  series: [
    {
      type: "pie",
      radius: ["40%", "80%"],
      label: {
        color: "white",
        position: "inside",
        formatter: (params: any, ticket: any, callback: any) => {
          console.log(params.percent);
          if (params.percent > 10) {
            return `${params.percent}%`;
          }
          return "";
        },
      },
      labelLine: {
        show: false,
      },
    },
  ],
};

@Component({
  selector: "app-customer-insights",
  templateUrl: "./customer-insights.component.html",
  styleUrls: ["./customer-insights.component.scss"],
})
export class CustomerInsightsComponent implements OnInit {
  customerInsights: any;
  filterForm!: FormGroup;
  competitorVisitFreq: any;
  daysSinceLastVisit: any;
  onPropVisitFreq: any;
  timeOnStation: any;
  onPropVisitFreqOptions: any;
  competitorVisitFreqOptions: any;
  daysSinceLastVisitOptions: any;
  timeOnStationOptions: any;
  allFacilities: any;
  private onComponentDestroy = new Subject();
  competitorVisits: any;
  competitorVisitsOptions: any;
  genderOptions: any;
  genders: any;

  constructor(
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private uiService: UiService,
    private AppService: AppService,
    private NotificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.allFacilities = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return {
            ...facility,
            selected: true,
          };
        })
      : [];
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.initData(this.route.snapshot.data.customerInsights);

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.reloadCustomerInsight();
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initData(customerInsights: any) {
    let timeOnStationCount = 0;
    let daysSinceLastVisitCount = 0;
    let competitorVisitsCount = 0;

    this.competitorVisitFreq = customerInsights.competitor_visit_frequency;
    this.daysSinceLastVisit = customerInsights.days_since_last_visit;
    this.onPropVisitFreq = customerInsights.on_property_visit_frequency;
    this.timeOnStation = customerInsights.time_on_station;
    this.competitorVisits = customerInsights.competitor_visits;
    this.genders = customerInsights.genders;

    this.onPropVisitFreq.reverse();

    this.competitorVisitFreq.reverse();

    this.onPropVisitFreqOptions = defaultsDeep(
      {
        yAxis: {
          data: this.onPropVisitFreq.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return label;
          }),
        },
        series: [
          {
            data: this.onPropVisitFreq.map((range: any) => {
              return range.count;
            }),
            label: {
              color: "#A1C700",
            },
          },
        ],
      },
      DEFUALT_HOR_BAR_CHART_OPTIONS,
    );

    this.competitorVisitFreqOptions = defaultsDeep(
      {
        yAxis: {
          data: this.competitorVisitFreq.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return label;
          }),
        },
        series: [
          {
            data: this.competitorVisitFreq.map((range: any) => {
              return range.count;
            }),
            itemStyle: { color: "#293a42", borderRadius: [0, 5, 5, 0] },
            label: {
              color: "#293a42",
            },
          },
        ],
      },
      DEFUALT_HOR_BAR_CHART_OPTIONS,
    );

    this.daysSinceLastVisitOptions = defaultsDeep(
      {
        xAxis: {
          data: this.daysSinceLastVisit.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return label;
          }),
        },
        series: [
          {
            data: this.daysSinceLastVisit.map((range: any) => {
              const data = {
                value: range.count,
                itemStyle: {
                  color: DAYS_SINCE_LAST_VISIT_COLORS[daysSinceLastVisitCount],
                },
              };
              daysSinceLastVisitCount++;
              return data;
            }),
            label: {
              formatter: (d: any) => {
                return shortenNumber(d.value);
              },
            },
          },
        ],
      },
      DEFAULT_BAR_CHART_OPTIONS,
    );

    this.timeOnStationOptions = defaultsDeep(
      {
        xAxis: {
          type: "category",
          boundaryGap: true,

          data: this.timeOnStation.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return { value: label, label };
          }),
        },
        series: [
          {
            data: this.timeOnStation.map((range: any) => {
              const data = {
                value: range.count,
                itemStyle: {
                  color: TIME_ON_STATION_COLORS[timeOnStationCount],
                },
              };
              timeOnStationCount++;
              return data;
            }),

            label: {
              formatter: (d: any) => {
                return shortenNumber(d.value);
              },
            },
          },
        ],
      },
      DEFAULT_BAR_CHART_OPTIONS,
    );

    this.competitorVisitsOptions = defaultsDeep(
      {
        xAxis: {
          type: "category",
          boundaryGap: true,

          data: this.competitorVisits.map((range: any) => {
            let label = "";
            if (range.range_min === range.range_max) {
              label = range.range_min;
            } else if (range.range_max === 9999999) {
              label = `${range.range_min}+`;
            } else {
              label = `${range.range_min} - ${range.range_max}`;
            }

            return { value: label, label };
          }),
        },
        series: [
          {
            data: this.competitorVisits.map((range: any) => {
              const data = {
                value: range.count,
                itemStyle: {
                  color: TIME_ON_STATION_COLORS[competitorVisitsCount],
                },
              };
              competitorVisitsCount++;
              return data;
            }),
            label: {
              formatter: (d: any) => {
                return shortenNumber(d.value);
              },
            },
          },
        ],
      },
      DEFAULT_BAR_CHART_OPTIONS,
    );

    this.genderOptions = defaultsDeep(
      {
        series: [
          {
            name: "Deliveries by category",
            data: [
              {
                name: "Male",
                value: this.genders.male_count,

                itemStyle: {
                  color: "#a1c700",
                },
              },
              {
                name: "Female",
                value: this.genders.female_count,

                itemStyle: {
                  color: "#7dc4fd",
                },
              },
              {
                name: "Unknown",
                value: this.genders.unknown_count,
                itemStyle: {
                  color: "#707070",
                },
              },
            ],
          },
        ],
      },
      DEFAULT_PIE_CHART_OPTIONS,
    );
  }

  reloadCustomerInsight() {
    if (
      this.filterForm.controls.startDate.value <
      this.filterForm.controls.endDate.value
    ) {
      this.filterForm.controls.endDate.value.setHours(23, 59, 59);

      const facilityIDs = this.allFacilities
        .filter((facility: any) => facility.selected)
        .map((facility: any) => facility.id);

      this.uiService
        .waitForEvent(
          this.AppService.getCustomerInsights(
            this.SessionService.currentAccountValue!.accountID,
            facilityIDs,
            this.filterForm.controls.startDate.value,
            this.filterForm.controls.endDate.value,
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (customerInsights) => {
            this.initData(customerInsights);
          },
          (err) => {
            this.NotificationService.openErrorSnackBar(
              "Error retrieving information",
            );
          },
        );
    }
  }

  onFacilityChange(facility: any) {
    this.reloadCustomerInsight();
  }
}
